import { GatewayChargePointDiagnosticsModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-point-diagnostics/models/outputs/gateway-charge-point-diagnostics.model';
import { differenceInSeconds } from 'date-fns';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useCpmsBoGetDiagnosticsOcppMutation } from '@app/core/service/cpms-bo-charge-point-ocpp.service';
import { useChargePointContext } from '@app/feature/charge-point/charge-point.context';
import {
  useCpmsBoGetDiagnosticsPaginatedQuery,
  useLazyCpmsBoDownloadChargePointDiagnosticQuery,
} from '@service/cpms-bo-charge-point-diagnostics.service';

import { downloadDiagnostic } from '../helper/diagnostic.helper';
import { ChargePointDiagnosticListView } from './charge-point-diagnostic-list.view';

export const ChargePointDiagnosticListController = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const chargePoint = useChargePointContext();
  const [getDiagnosticsOcppMutation] = useCpmsBoGetDiagnosticsOcppMutation();
  const [lazyDownloadChargePointDiagnostic] =
    useLazyCpmsBoDownloadChargePointDiagnosticQuery();
  const [pollingInterval, setPollingInterval] = useState(0);
  const [diagnosticRequestPending, setDiagnosticRequestPending] =
    useState(false);
  const [diagnosticRequestStale, setDiagnosticRequestStale] = useState(true);

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const {
    isFetching,
    isSuccess,
    data: diagnostics,
    refetch,
  } = useCpmsBoGetDiagnosticsPaginatedQuery(
    {
      chargePointId: chargePoint.id,
      limit: 10,
      offset: 0,
      sortField: 'createdAt',
      sortOrder: -1,
    },
    {
      pollingInterval,
    },
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleDownloadDiagnostic = async (
    diagnostic: GatewayChargePointDiagnosticsModel,
  ) => {
    try {
      const {
        isError: downloadChargePointDiagnosticIsError,
        isSuccess: downloadChargePointDiagnosticIsSuccess,
        data: downloadChargePointDiagnosticData,
        error: downloadChargePointDiagnosticError,
      } = await lazyDownloadChargePointDiagnostic({
        chargePointDiagnosticId: diagnostic.id,
        responseType: diagnostic.mimeType ?? 'application/octet-stream',
      });

      if (downloadChargePointDiagnosticIsError) {
        throw new Error(JSON.stringify(downloadChargePointDiagnosticError));
      }

      if (downloadChargePointDiagnosticIsSuccess) {
        await downloadDiagnostic(diagnostic, downloadChargePointDiagnosticData);
        toast.success(t('$.charge-point-diagnostic-download.success'));
      }
    } catch (error: any) {
      toast.error(
        t('$.charge-point-diagnostic-download.error', {
          error: error.toString(),
        }),
      );
    }
  };

  const handleGetDiagnostic = () => {
    setDiagnosticRequestPending(true);
    setDiagnosticRequestStale(false);

    getDiagnosticsOcppMutation({ chargePointId: chargePoint.id })
      .unwrap()
      .then(() => {
        toast.success(t('$.charge-point-diagnostic-create.success'));
      })
      .catch((error) => {
        toast.error(
          t('$.charge-point-diagnostic-create.error', {
            error,
          }),
        );
      })
      .finally(async () => {
        setDiagnosticRequestPending(false);
        refetch();
      });
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  useEffect(() => {
    if (isSuccess) {
      const diagnostic = diagnostics.docs.at(0);

      if (diagnostic) {
        setPollingInterval(
          diagnosticRequestStale || diagnostic.uploadedAt ? 0 : 2000,
        );
      }
    }
  }, [isSuccess, diagnostics, diagnosticRequestStale]);

  useEffect(() => {
    if (isSuccess && !isFetching) {
      const diagnostic = diagnostics.docs.at(0);

      if (!diagnostic) {
        setDiagnosticRequestStale(true);
        return;
      }

      setDiagnosticRequestStale(
        !!diagnostic.uploadedAt ||
          differenceInSeconds(new Date(), diagnostic.createdAt) > 10,
      );
    }
  }, [diagnostics, isSuccess, isFetching]);

  return (
    <ChargePointDiagnosticListView
      diagnostics={diagnostics?.docs}
      diagnosticRequestPending={diagnosticRequestPending}
      diagnosticRequestStale={diagnosticRequestStale}
      handleDownloadDiagnostic={handleDownloadDiagnostic}
      handleGetDiagnostic={handleGetDiagnostic}
    />
  );
};
